import React, {useEffect, useState} from 'react';
import {Table, Button, Form, Input, Modal, Space, message, Select, Row, Col, Checkbox} from 'antd';
import {FilterOptionDto, LocateDto, LocateDtoLocateOptionEnum, LocateDtoRoleEnum, ParsingControllerApi} from "../../../../api";
import {DeleteOutlined, EditOutlined, PlusOutlined} from "@ant-design/icons";
import {getLabelWithNameAndInfotext} from "../../../../utils/Utils";
import {useKeycloakHeader} from "../../../../config/Config";
import {locateDtoToList} from "../../../../utils/LocatorUtils";


const LocatorList = ({locateDtoList, onChange}) => {
    const {Option} = Select;
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [form] = Form.useForm();
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [importLocatorValue, setImportLocatorValue] = useState('');
    const headers = useKeycloakHeader();
    const parsingControllerApi = new ParsingControllerApi();

    // Ausgewählte LocateOption (wichtig weil die Option Role ein extra Feld benötigt)
    const [selectedLocateOption, setSelectedLocateOption] = useState<string | undefined>(undefined);

    // Alle LocateOptionen und AriaRoleOptionen
    const allLocateOptions = Object.values(LocateDtoLocateOptionEnum);
    const ariaRoleOptions = Object.values(LocateDtoRoleEnum);
    const allFilterOptions = ["", "HAS_TEXT", "HAS_NOT_TEXT"];


    const columns = [
        {
            title: 'Option',
            dataIndex: 'locateOption',
            key: 'locateOption',
        },
        {
            title: 'Rolle',
            dataIndex: 'role',
            key: 'role',
        },
        {
            title: 'Wert',
            dataIndex: 'checkValue',
            key: 'checkValue',
        },
        {
            title: 'Index',
            dataIndex: 'nthElement',
            key: 'nthElement',
        },
        {
            title: 'Eindeutig',
            dataIndex: 'exact',
            key: 'exact',
            render: (text: boolean) => (text ? 'Ja' : 'Nein'),
        },
        {
            title: 'Filter',
            dataIndex: 'filterOption',
            key: 'filterOption',
            render: (text, record: LocateDto) => {

                // Überprüfe ob hasText vorhanden ist
                if (record.filterOption?.hasText) {
                    return "HAS_TEXT: " + record.filterOption.hasText;
                }

                // Überprüfe ob hasNotText vorhanden ist
                if (record.filterOption?.hasNotText) {
                    return "HAS_NOT_TEXT: " + record.filterOption.hasNotText;
                }

                // Falls weder hasText noch hasNotText vorhanden sind
                return "Kein Filter";
            }
        },
        {
            title: 'Aktionen',
            key: 'actions',
            render: (_: any, record: LocateDto, index: number) => (
                <Space size="middle">
                    <Button type="link" onClick={() => editLocator(index)}><EditOutlined/></Button>
                    <Button type="link" danger onClick={() => deleteLocator(index)}><DeleteOutlined/></Button>
                </Space>
            ),
        },
    ];

    const updateLocatorsForParent = (locators: LocateDto[]) => {
        onChange(locators);
    }

    // Wenn eine LocateOption ausgewählt wurde wird es ein Update geben.
    const handleLocateOptionChange = (value: string) => {
        setSelectedLocateOption(value);
    };

    const addLocator = (values: LocateDto, formValues) => {
        const newLocator = {...values};// ID basierend auf Timestamp generieren

        if (formValues.filterOption === "HAS_TEXT") newLocator.filterOption = {hasText: formValues.filterValue} as FilterOptionDto;
        if (formValues.filterOption === "HAS_NOT_TEXT") newLocator.filterOption = {hasNotText: formValues.filterValue} as FilterOptionDto;

        let updatedLocators;
        if (locateDtoList) {
            updatedLocators = [...locateDtoList, newLocator];
        } else {
            updatedLocators = [newLocator];
        }

        updateLocatorsForParent(updatedLocators);
        form.resetFields();
        setIsModalVisible(false);
        message.success('Locator hinzugefügt!');
    };

    const editLocator = (index: number) => {
        const locatorToEdit = locateDtoList[index];
        form.setFieldsValue(locatorToEdit);
        setSelectedLocateOption(locatorToEdit.locateOption);
        setFieldValuesForFilter(locatorToEdit);
        setEditingIndex(index);
        setIsModalVisible(true);
    };

    const setFieldValuesForFilter = (locateDto: LocateDto) => {
        if (locateDto.filterOption?.hasText) {
            form.setFieldValue('filterValue', locateDto.filterOption.hasText);
            form.setFieldValue('filterOption', "HAS_TEXT");
        }
        if (locateDto.filterOption?.hasNotText) {
            form.setFieldValue('filterValue', locateDto.filterOption.hasNotText);
            form.setFieldValue('filterOption', "HAS_NOT_TEXT");
        }
        if(locateDto.filterOption?.pattern) {
            form.setFieldValue('pattern', locateDto.filterOption.pattern);
        }
    }

    const deleteLocator = (index: number) => {
        const updatedLocators = locateDtoList.filter((_, i) => i !== index);
        updateLocatorsForParent(updatedLocators);
        message.success('Locator gelöscht!');
    };

    const handleModalOk = () => {
        form.validateFields()
            .then(values => {
                if (editingIndex !== null) {
                    const updatedLocators = locateDtoList.map((item, index) => {
                        if (index === editingIndex) {
                            const newLocator = {...values};

                            // Wandle die beiden if-Abfragen in die Map-Funktion um
                            newLocator.filterOption = ["HAS_TEXT", "HAS_NOT_TEXT"].includes(values.filterOption)
                                ? values.filterOption === "HAS_TEXT"
                                    ? {hasText: values.filterValue} as FilterOptionDto
                                    : {hasNotText: values.filterValue} as FilterOptionDto
                                : null;
                            console.log("Pattern: ", values.pattern);
                            if(newLocator.filterOption && values.pattern){
                                newLocator.filterOption.pattern = values.pattern;
                            }

                            return newLocator;
                        }
                        return item;
                    });

                    updateLocatorsForParent(updatedLocators);
                    setEditingIndex(null);
                } else {
                    addLocator(values, values);
                }
                setIsModalVisible(false);
            })
            .catch(info => {
                console.log('Validate Failed:', info);
            });
    };

    const importLocator = () => {
        parsingControllerApi.getParsedLocator(importLocatorValue, headers).then((axiosResponse) => {
            // Daten der API
            const locateDto: LocateDto = axiosResponse.data;
            if (axiosResponse.status === 200) {
                const locateDtos = locateDtoToList(locateDto);
                updateLocatorsForParent(locateDtos);
            }
        }).catch(error => {
            message.error("Der angegebene Locator konnte nicht übersetzt werden!");
        });
    }

    return (
        <>
            <div style={{display: 'flex', alignItems: 'center', marginBottom: 16}}>
                <Button type="primary" onClick={() => setIsModalVisible(true)} style={{marginRight: 16}}>
                    <PlusOutlined/> Neu
                </Button>

                <label style={{marginRight: 8, paddingLeft: "30px"}}> Import:</label>

                <Space.Compact style={{flexGrow: 1, display: 'flex'}}>
                    <Input
                        id="importField"
                        style={{flexGrow: 1}}
                        placeholder="Generierten Locator einfügen"
                        onChange={(e) => setImportLocatorValue(e.target.value)}
                    />

                    <Button
                        type="primary"
                        htmlType="button"
                        style={{marginLeft: 8}}
                        onClick={importLocator}
                    >
                        Import
                    </Button>
                </Space.Compact>
            </div>
            <div style={{display: 'flex', justifyContent: 'center', marginTop: 16}}>
                <Table
                    columns={columns}
                    dataSource={locateDtoList}
                    rowKey="id"
                    style={{width: '100%', minWidth: '400px'}}
                    locale={{emptyText: 'Keine Daten vorhanden'}}
                />
            </div>

            <Modal
                title={editingIndex !== null ? 'Bearbeiten' : 'Hinzufügen'}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={() => {
                    setIsModalVisible(false);
                    setEditingIndex(null);
                    form.resetFields();
                }}
                okText="Bestätigen"
                cancelText="Abbrechen"
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="locateOption" label="Locate-Option" rules={[{required: true}]}>
                        <Select
                            placeholder="Wähle eine Locator-Option"
                            onChange={handleLocateOptionChange}
                            allowClear
                        >
                            {allLocateOptions.map(option => (
                                <Option key={option} value={option}>
                                    {option}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    {selectedLocateOption === 'ROLE' && (
                        <Form.Item name="role" label="Rolle" rules={[{required: true}]}>
                            <Select
                                placeholder="Wähle eine Rolle"
                                allowClear
                            >
                                {ariaRoleOptions.map(option => (
                                    <Option key={option} value={option}>
                                        {option}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                    <Form.Item name="checkValue"
                               label={getLabelWithNameAndInfotext(
                                   "Locator-Wert",
                                   "Hier wird der Wert für die jeweilige Locate-Option angegeben"
                               )} rules={[{required: true}]}>
                        <Input/>
                    </Form.Item>
                    <Form.Item name="nthElement"
                               label={getLabelWithNameAndInfotext(
                                   "Index-Element",
                                   "Falls der Locator eine Liste zurückgibt, kann hier der Index des gewünschten Zielelements angegeben werden."
                               )}
                               rules={[
                                   {
                                       type: 'integer',
                                       message: 'Bitte geben Sie eine ganze Zahl ein.',
                                       transform: value => (value ? parseInt(value, 10) : undefined),
                                   },
                               ]}>
                        <Input/>
                    </Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="filterOption"
                                label="Filter-Option"
                            >
                                <Select
                                    placeholder="Wähle eine Filter-Option"
                                    allowClear
                                >
                                    {allFilterOptions.map(option => (
                                        <Option key={option} value={option}>
                                            {option}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="filterValue"
                                label="Filter-Wert"
                            >
                                <Input placeholder="Gib den Filterwert ein"/>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item
                        name="pattern"
                        valuePropName="checked"
                        style={{ marginBottom: '8px' }}
                    >
                        <Checkbox>
                            Patter?{' '}
                            <span style={{fontSize: '12px', color: 'gray'}}>
                              (Ist der Filter-Wert ein Regulärer Ausdruck?)
                            </span>
                        </Checkbox>
                    </Form.Item>
                    <Form.Item
                        name="exact"
                        valuePropName="checked"
                    >
                        <Checkbox>
                            Eindeutig?{' '}
                            <span style={{fontSize: '12px', color: 'gray'}}>
                              (Das zu lokalisierende Element muss eindeutig übereinstimmen)
                            </span>
                        </Checkbox>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default LocatorList;
